import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {TranslationGroup} from 'biojs/Sequence';
import {Translation, TranslationAttributes} from 'biojs/Translation';
import {Sequence} from 'components/Sequence';
import * as CrisprConstants from 'constants/CRISPRConstants';
import {Strand} from 'constants/MolbioConstants';
import {SequenceVersion} from 'constants/SequenceConstants';
import {editTranslationRegionGroup, TranslationRegionModificationType} from 'dao/SequenceDAO';
import * as sequtil from 'molbio/SeqUtil';
import {updateModelsAfterTranslationChange} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
// @ts-expect-error
import * as LinearSelectionUtil from 'util/LinearSelectionUtil';

import * as EditDistance from './EditDistance';
import EditsTable from './EditsTable';
import HRTemplateActions from './HRTemplateActions';
import HRTemplateStore from './HRTemplateStore';

const {DESIRED_HR_TEMPLATE_LENGTH, HR_TEMPLATE_STEP, MINIMUM_HR_TEMPLATE_LENGTH, HR_TEMPLATE_TAB_ID} =
  CrisprConstants;

export interface Props {
  sequence: Sequence;
  savedVersions: {[step in CrisprConstants.HRTemplateStep]: SequenceVersion & {bases: string}};
  armRegion: {
    start: number;
    end: number;
  };
  resettingToVersion: boolean;
  modifyingSequence: boolean;
  isHRTemplateLoading: boolean;
  selection: {
    start: number;
    end: number | null;
  };
}

class SelectArmsStep extends React.Component<Props> {
  componentDidMount(): void {
    const {start, end} = this.props.armRegion;
    if (start == null || end == null) {
      this.suggestArms(false);
    } else {
      this.suggestArms();
    }
    this.props.sequence.addLockingTab({tabId: HR_TEMPLATE_TAB_ID, tabName: 'HR template design'});
  }

  componentDidUpdate(prevProps: Props): void {
    const {start, end} = this.props.selection;
    if (!isEqual(this.props.selection, prevProps.selection)) {
      const pendingSelection = HRTemplateStore.getPendingSelection(this.props.sequence.id);
      if (pendingSelection != null && pendingSelection.start === start && pendingSelection.end === end) {
        HRTemplateActions.updatePendingSelection(this.props.sequence.id, null);
      } else {
        HRTemplateActions.update(this.props.sequence.id, {
          armRegion: {
            start: this.props.selection.start,
            end: this.props.selection.end,
          },
        });
      }
    }
  }

  setArmRegion = (start: number | null, end: number | null): void => {
    HRTemplateActions.update(this.props.sequence.id, {
      armRegion: {start, end},
    });
    let validSelection = false;
    if (LinearSelectionUtil.isValidRangeAndNotNull(start, end, this.props.sequence.length(), false)) {
      this.props.sequence.selection().setEndpoints(start, end);
      validSelection = true;
    } else if (
      end == null &&
      LinearSelectionUtil.baseInRangeAndNotNull(start, 0, this.props.sequence.length())
    ) {
      this.props.sequence.selection().reset(start);
      validSelection = true;
    }
    if (validSelection) {
      HRTemplateActions.updatePendingSelection(this.props.sequence.id, {start, end});
      this.props.sequence.trigger('jump', start);
    }
  };

  suggestArms = (warn: boolean = true): void => {
    const originalBases = this.props.savedVersions[HR_TEMPLATE_STEP.INTRODUCE_EDITS].bases;
    const {index, original, final} = EditDistance.extractModifiedRegion(
      originalBases,
      this.props.sequence.bases()
    );
    if (!original && !final && warn) {
      notifications.warn('Could not detect modified region for arm suggestion.', {id: 'no-modifications'});
      return;
    }

    const idealArmLength = 100 - final.length / 2;

    // Excess goes to the upstream arm.
    let start = Math.floor(index - idealArmLength);
    let end = Math.floor(index + final.length + idealArmLength);
    // If one arm is clipped, add the clipped amount to the other arm to preserve a total length of 200.
    if (start < 0) {
      end += -start;
      start = 0;
    }
    if (end > this.props.sequence.length()) {
      start -= end - this.props.sequence.length();
      end = this.props.sequence.length();
    }
    start = Math.max(0, start);
    end = Math.min(this.props.sequence.length(), end);

    assert(
      end - start === DESIRED_HR_TEMPLATE_LENGTH || this.props.sequence.length() < DESIRED_HR_TEMPLATE_LENGTH,
      'expected sequence to have desired HR template length'
    );

    this.setArmRegion(start, end);
  };

  _deleteBases = (start: number, end: number): JQueryPromise<unknown> => {
    if (start !== end) {
      return this.props.sequence.deleteBases(start, end);
    } else {
      return $.Deferred().resolve().promise();
    }
  };

  trimTranslations = async (start: number, end: number): Promise<void> => {
    // For the first translation that will be kept, delete and add a new one to the translation group
    // with a modified start that begins at the first full amino acid, so the translation sequence doesn't
    // change after we trim the bases

    const {sequence} = this.props;
    const newTranslationsByGroupId: {
      [id: number]: Array<{start: number; end: number; id: number; strand: Strand}>;
    } = {};
    for (const translationGroup of sequence.getTranslationGroups()) {
      let beforeLength = 0;
      newTranslationsByGroupId[translationGroup.id] = [];
      for (const translation of translationGroup.translations) {
        if (sequtil.isRangeOverlap(start, end, translation.start, translation.end)) {
          // Figure out where the first full amino acid of this translation starts
          let newStart;
          const firstFullAAMod = beforeLength % 3;
          const firstFullAAStart = translation.start + ((3 - firstFullAAMod) % 3);
          if (start <= firstFullAAStart) {
            newStart = firstFullAAStart;
          } else {
            // Figure out where the first full amino acid of the trimmed translation should start
            const trimmedMod = (start - firstFullAAStart) % 3;
            newStart = start + ((3 - trimmedMod) % 3);
          }

          if (newStart === translation.start) {
            break;
          }

          if (newStart < translation.end) {
            newTranslationsByGroupId[translationGroup.id].push({
              start: newStart,
              end: translation.end,
              strand: translation.strand,
              id: translation.id,
            });
          }
          break;
        } else {
          beforeLength += sequtil.length(translation.start, translation.end, sequence.length());
        }
      }
    }

    const newGroupsToUpdate: Array<TranslationGroup> = [];
    let newRegionsToUpdate: Array<Translation | TranslationAttributes> = [];
    for (const [groupId, translations] of Object.entries(newTranslationsByGroupId)) {
      if (translations.length === 0) {
        continue;
      }
      const {translationGroup, translationRegions} = await editTranslationRegionGroup(
        sequence.id,
        parseInt(groupId, 10),
        {
          regions: translations.map(({start, end, strand, id}) => ({
            modificationType: TranslationRegionModificationType.UPDATE,
            start,
            end,
            strand,
            id,
          })),
        }
      );
      newGroupsToUpdate.push(translationGroup);
      newRegionsToUpdate = newRegionsToUpdate.concat(translationRegions);
    }
    if (newGroupsToUpdate.length > 0 || newRegionsToUpdate.length > 0) {
      updateModelsAfterTranslationChange(sequence, newGroupsToUpdate, newRegionsToUpdate, []);
    }
  };

  trimToSelectedRange = async (armRegionStart: number, armRegionEnd: number): Promise<void> => {
    if (
      !LinearSelectionUtil.isValidRangeAndNotNull(
        armRegionStart,
        armRegionEnd,
        this.props.sequence.length(),
        false
      )
    ) {
      notifications.error('Please select a valid range for HR arms.');
      return;
    }
    if (armRegionEnd - armRegionStart < MINIMUM_HR_TEMPLATE_LENGTH) {
      notifications.error(`Your selected arms are less than ${MINIMUM_HR_TEMPLATE_LENGTH} bp.`);
      return;
    }

    const originalBases = this.props.savedVersions[HR_TEMPLATE_STEP.INTRODUCE_EDITS].bases;
    const {index, final} = EditDistance.extractModifiedRegion(originalBases, this.props.sequence.bases());
    if (
      index < this.props.sequence.length() &&
      (armRegionStart > index || armRegionEnd < index + final.length)
    ) {
      notifications.error('Selected range must contain all modified regions.');
      return;
    }

    if (armRegionEnd - armRegionStart < DESIRED_HR_TEMPLATE_LENGTH) {
      notifications.warn(`Your selected arms are less than ${DESIRED_HR_TEMPLATE_LENGTH} bp.`);
    }

    HRTemplateActions.update(this.props.sequence.id, {
      modifyingSequence: true,
    });

    try {
      await this.trimTranslations(armRegionStart, armRegionEnd);
      await this._deleteBases(armRegionEnd, this.props.sequence.length());
      await this._deleteBases(0, armRegionStart);

      this.props.sequence.selection().setEndpoints(0, this.props.sequence.length());
      this.props.sequence.trigger('jump', 0);
      const savedVersions = _.object([
        [HR_TEMPLATE_STEP.REMOVE_TARGET_SITE, this.props.sequence.getActiveVersionId()],
      ]);

      HRTemplateActions.serverUpdate(this.props.sequence.id, {
        step: HR_TEMPLATE_STEP.REMOVE_TARGET_SITE,
        savedVersions,
        armRegion: {
          start: armRegionStart,
          end: armRegionEnd,
        },
      });
    } finally {
      HRTemplateActions.update(this.props.sequence.id, {
        modifyingSequence: false,
      });
    }
  };

  renderLength = (): string => {
    const {start, end} = this.props.armRegion;
    if (start == null || end == null) {
      return 'n/a';
    } else {
      const length = sequtil.length(start, end, this.props.sequence.length());
      return `${length} bp`;
    }
  };

  renderArmLengths = (): JSX.Element => {
    const {start, end} = this.props.armRegion;
    if (start == null || end == null) {
      return <div />;
    }

    const originalBases = this.props.savedVersions[HR_TEMPLATE_STEP.INTRODUCE_EDITS].bases;
    const {index, final} = EditDistance.extractModifiedRegion(originalBases, this.props.sequence.bases());

    return (
      <div>
        <div>
          {'Left arm length: '}
          {start < index ? `${index - start} bp` : 'N/A'}
        </div>
        <div>
          {'Right arm length: '}
          {index + final.length < end ? `${end - (index + final.length)} bp` : 'N/A'}
        </div>
      </div>
    );
  };

  render(): JSX.Element {
    const startValue = sequtil.modelStartToDisplay(
      this.props.armRegion.start,
      this.props.sequence.startIndex()
    );
    const endValue = sequtil.modelEndToDisplay(
      this.props.armRegion.end,
      this.props.sequence.length(),
      this.props.sequence.startIndex()
    );
    return (
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          analytics.track('hr_template', 'completed_step_select_arms');
          await this.trimToSelectedRange(this.props.armRegion.start, this.props.armRegion.end);
        }}
      >
        <legend>
          <div className="u-large-text">Step 2: Adjust HR arms</div>
        </legend>
        <div className="alert alert-info">
          <div className="u-small-spacing-bottom">
            <strong>Adjust the region to use as the HR template</strong>
            {' by clicking and dragging the ends of the selection on the sequence map.'}
          </div>
          <div>
            <em>
              {`A ${DESIRED_HR_TEMPLATE_LENGTH} bp region around your mutations has already been selected. `}
              At least 50 bp on each side flanking the mutations is recommended.
            </em>
          </div>
        </div>
        <legend>
          <h5>Template region</h5>
        </legend>
        <FormGroup>
          <input
            className="u-inline-form-input-sm u-small-spacing-right"
            type="number"
            placeholder="Start"
            value={startValue != null ? startValue : undefined}
            onChange={(e) => {
              let start: number | null = parseInt(e.target.value, 10);
              if (Number.isNaN(start)) {
                start = null;
              } else {
                start = sequtil.displayStartToModel(start, this.props.sequence.startIndex());
              }
              this.setArmRegion(start, this.props.armRegion.end);
            }}
          />
          -
          <input
            className="u-inline-form-input-sm u-small-spacing-left"
            type="number"
            placeholder="End"
            value={endValue != null ? endValue : undefined}
            onChange={(e) => {
              let end: number | null = parseInt(e.target.value, 10);
              if (Number.isNaN(end)) {
                end = null;
              } else {
                end = sequtil.displayEndToModel(
                  end,
                  this.props.sequence.length(),
                  this.props.sequence.startIndex()
                );
              }
              this.setArmRegion(this.props.armRegion.start, end);
            }}
          />
          <a
            className="u-input-line-height u-spacing-left"
            onClick={() => {
              this.suggestArms();
            }}
          >
            Reset to default
          </a>
        </FormGroup>
        <div className="faded u-spacing-bottom">
          <div>
            {'Template Length: '}
            {this.renderLength()}
          </div>
          {this.renderArmLengths()}
        </div>
        <EditsTable
          originalBases={this.props.savedVersions[HR_TEMPLATE_STEP.INTRODUCE_EDITS].bases}
          currentBases={this.props.savedVersions[HR_TEMPLATE_STEP.SELECT_ARMS].bases}
          sequence={this.props.sequence}
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          type="submit"
          className="pull-right"
          disabled={this.props.modifyingSequence || this.props.isHRTemplateLoading}
          text={this.props.modifyingSequence || this.props.isHRTemplateLoading ? 'Loading...' : 'Next'}
        />
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          disabled={this.props.resettingToVersion || this.props.isHRTemplateLoading}
          onClick={HRTemplateActions.backToStep.bind(
            null,
            this.props.sequence.id,
            HR_TEMPLATE_STEP.INTRODUCE_EDITS
          )}
          text={this.props.isHRTemplateLoading ? 'Loading...' : 'Back'}
        />
      </form>
    );
  }
}

export default SelectArmsStep;
